import { createFileRoute } from "@tanstack/react-router";
import { Sparkles, Users, BookOpen, Target, Heart, ShieldCheck, Megaphone } from "lucide-react";
import { PageContentEditor, type PageSectionDef } from "@/components/admin/PageContentEditor";
import { getAdminAboutPage, updateAdminAboutPage } from "@/lib/admin/pages.functions";

export const Route = createFileRoute("/_admin/admin/about")({
  component: AdminAboutPage,
});

const SECTIONS: PageSectionDef[] = [
  {
    title: "Page header",
    icon: Sparkles,
    description: "The eyebrow, headline, intro and banner shown at the top of the About page.",
    fields: [
      { key: "hero_eyebrow", label: "Eyebrow", help: "Small label above the headline." },
      { key: "hero_title", label: "Headline", help: "Main page headline." },
      { key: "hero_intro", label: "Intro", help: "Short paragraph under the headline.", type: "textarea" },
      { key: "hero_image_url", label: "Hero image", help: "Wide banner directly under the page header.", type: "image" },
      { key: "hero_image_caption", label: "Hero image caption", help: "Caption shown under the hero banner." },
    ],
  },
  {
    title: "Our story",
    icon: BookOpen,
    description: "The story block describing how the kitchen started.",
    fields: [
      { key: "story_eyebrow", label: "Eyebrow", help: "Small label above the story heading." },
      { key: "story_heading", label: "Heading", help: "Story section heading." },
      { key: "story_body", label: "Story paragraph 1", help: "First paragraph of the story.", type: "textarea" },
      { key: "story_body_2", label: "Story paragraph 2", help: "Second paragraph of the story.", type: "textarea" },
      { key: "story_image_url", label: "Story image", help: "Banner shown with the story section.", type: "image" },
      { key: "story_image_caption", label: "Story image caption", help: "Caption shown under the story banner." },
    ],
  },
  {
    title: "Founder",
    icon: Users,
    description: "Founder name, role, quote and the portrait shown beside the story.",
    fields: [
      { key: "founder_name", label: "Founder name", help: "Displayed beside the founder portrait." },
      { key: "founder_role", label: "Founder designation", help: "For example: Founder & Head Chef." },
      { key: "founder_quote", label: "Founder quote", help: "A short quote from the founder.", type: "textarea" },
      { key: "founder_image_url", label: "Founder image", help: "Portrait chosen from the Media Library.", type: "image" },
      { key: "founder_image_caption", label: "Founder image caption", help: "Caption shown on the portrait." },
    ],
  },
  {
    title: "Mission & vision",
    icon: Target,
    description: "The mission and vision block.",
    fields: [
      { key: "mission_eyebrow", label: "Eyebrow", help: "Small label above the mission block." },
      { key: "mission_heading", label: "Mission heading", help: "Heading of the mission card." },
      { key: "mission_body", label: "Mission text", help: "Mission statement.", type: "textarea" },
      { key: "vision_heading", label: "Vision heading", help: "Heading of the vision card." },
      { key: "vision_body", label: "Vision text", help: "Vision statement.", type: "textarea" },
      { key: "mission_image_url", label: "Mission image", help: "Banner shown with the mission and vision block.", type: "image" },
      { key: "mission_image_caption", label: "Mission image caption", help: "Caption shown under the mission banner." },
    ],
  },
  {
    title: "Values",
    icon: Heart,
    description: "The values section further down the page.",
    fields: [
      { key: "values_eyebrow", label: "Eyebrow", help: "Small label above the values heading." },
      { key: "values_heading", label: "Heading", help: "Values section heading." },
      { key: "values_intro", label: "Intro", help: "Short paragraph introducing the values.", type: "textarea" },
      { key: "values_image_url", label: "Values image", help: "Banner shown with the values section.", type: "image" },
      { key: "values_image_caption", label: "Values image caption", help: "Caption shown under the values banner." },
    ],
  },
  {
    title: "Why customers trust us",
    icon: ShieldCheck,
    description: "The trust block listing kitchen and sourcing standards.",
    fields: [
      { key: "trust_heading", label: "Heading", help: "Trust section heading." },
      { key: "trust_intro", label: "Intro", help: "Short paragraph introducing the trust points.", type: "textarea" },
    ],
  },
  {
    title: "Closing call to action",
    icon: Megaphone,
    description: "The closing banner and buttons just above the footer.",
    fields: [
      { key: "cta_eyebrow", label: "Eyebrow", help: "Small label above the closing headline." },
      { key: "cta_heading", label: "Headline", help: "Closing call-to-action headline." },
      { key: "cta_description", label: "Description", help: "Supporting sentence under the headline.", type: "textarea" },
      { key: "cta_primary_text", label: "Primary button text", help: "Label of the main button." },
      { key: "cta_primary_link", label: "Primary button link", help: "Internal path such as /meal-plans, or an https:// URL." },
      { key: "cta_secondary_text", label: "Secondary button text", help: "Label of the second button." },
      { key: "cta_secondary_link", label: "Secondary button link", help: "Internal path such as /contact, or an https:// URL." },
      { key: "cta_image_url", label: "Closing banner", help: "Image above the closing call to action.", type: "image" },
      { key: "cta_image_caption", label: "Closing banner caption", help: "Caption shown under the closing banner." },
    ],
  },
];

function AdminAboutPage() {
  return (
    <PageContentEditor
      title="About Page"
      description="Edit every section of the About page. Text and imagery live together, and images are picked from the Media Library."
      sections={SECTIONS}
      queryKey="admin-about-content"
      load={getAdminAboutPage as never}
      save={updateAdminAboutPage as never}
    />
  );
}
